import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Input, Select } from "../../../ui";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import { formatSar } from "../../../../utils/billingMoney";
import {
  createSubscriptionAPI,
  listBillingPlansAPI,
  listBillingStoresAPI,
  previewSubscriptionAPI,
} from "../../../../utils/api/billing";
import { labelFor, useBillingAdminText } from "../billingAdminI18n";

const CreateSubscriptionModal = ({ type = "platform", initialStoreId = null, onCancel, onCreated }) => {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const [stores, setStores] = useState([]);
  const [plans, setPlans] = useState([]);
  const [storeId, setStoreId] = useState(initialStoreId || "");
  const [planId, setPlanId] = useState("");
  const [branchIds, setBranchIds] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [storeResult, planResult] = await Promise.all([
          listBillingStoresAPI(token),
          listBillingPlansAPI(token, { status: "active", limit: 100 }),
        ]);
        setStores(storeResult.data || []);
        setPlans(planResult.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const selectedStore = useMemo(
    () => stores.find((store) => store.id === storeId),
    [stores, storeId],
  );
  const typedPlans = useMemo(() => plans.filter((plan) => plan.type === type), [plans, type]);
  const selectedPlan = useMemo(
    () => typedPlans.find((plan) => plan._id === planId),
    [typedPlans, planId],
  );

  const body = { storeId, planId, type, branchIds, startDate, endDate };
  const hasValidPeriod = Boolean(startDate) && Boolean(endDate) && startDate <= endDate;

  const loadPreview = async () => {
    if (!storeId || !planId) {
      setError(text.subscriptions.selectRestaurantAndPlan);
      return;
    }
    if (!hasValidPeriod) {
      setError(startDate && endDate ? text.subscriptions.endDateBeforeStartDate : text.subscriptions.selectStartAndEndDate);
      return;
    }
    setPreviewing(true);
    setError("");
    try {
      const result = await previewSubscriptionAPI(token, body);
      setPreview(result.data);
    } catch (err) {
      setPreview(null);
      setError(err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const createDraft = async () => {
    if (!can("billing.subscription.create")) return;
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await createSubscriptionAPI(token, body);
      await onCreated();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const toggleBranch = (branchId) => {
    setPreview(null);
    setBranchIds((current) =>
      current.includes(branchId)
        ? current.filter((item) => item !== branchId)
        : [...current, branchId],
    );
  };

  return (
    <div className="billing-modal-backdrop">
      <div className="billing-modal">
        <h3>{text.subscriptions.createTitle} — {type === "integration" ? text.subscriptions.ledgerLabels.intgStatus : text.subscriptions.ledgerLabels.ludStatus}</h3>
        <p>{text.subscriptions.createDescription}</p>
        {error && <div className="billing-error">{error}</div>}
        {loading ? (
          <div className="billing-loading">{text.common.loading}</div>
        ) : (
          <div className="billing-plan-modal-form">
            {initialStoreId ? (
              <div className="billing-preview-box">
                <span>{text.common.restaurant}</span>
                <strong>{selectedStore?.businessName || selectedStore?.id || initialStoreId}</strong>
              </div>
            ) : (
              <Select
                label={text.common.restaurant}
                value={storeId}
                options={stores.map((store) => ({
                  value: store.id,
                  label: `${store.businessName || store.id} (${store.id})`,
                }))}
                onChange={(event) => {
                  setStoreId(event.target.value);
                  setBranchIds([]);
                  setPreview(null);
                }}
                placeholder={text.invoices.selectRestaurant}
              />
            )}
            <Select
              label={text.subscriptions.activePlan}
              value={planId}
              options={typedPlans.map((plan) => ({
                value: plan._id,
                label: `${plan.code} v${plan.version} - ${plan.nameEn} - ${formatSar(plan.basePriceMinor, lang)}`,
              }))}
              onChange={(event) => {
                setPlanId(event.target.value);
                setPreview(null);
              }}
              placeholder={text.subscriptions.selectPlan}
            />
            {selectedPlan && (
              <div className="billing-preview-box">
                <strong>
                  {selectedPlan.code} v{selectedPlan.version}
                </strong>
                <span>
                  {labelFor(text.cycles, selectedPlan.billingCycle)} |{" "}
                  {text.subscriptions.includedBranches}:{" "}
                  {selectedPlan.includedBranchCount} | {text.plans.extraBranch}:{" "}
                  {formatSar(selectedPlan.extraBranchPriceMinor, lang)}
                </span>
              </div>
            )}
            <div className="billing-form-grid">
              <Input
                type="date"
                label={text.subscriptions.startDate}
                required
                value={startDate}
                onChange={(event) => {
                  setStartDate(event.target.value);
                  setPreview(null);
                }}
              />
              <Input
                type="date"
                label={text.subscriptions.endDate}
                required
                min={startDate || undefined}
                value={endDate}
                onChange={(event) => {
                  setEndDate(event.target.value);
                  setPreview(null);
                }}
              />
            </div>
            {selectedStore && (
              <div className="billing-branch-list">
                <strong>{text.subscriptions.branchCoverage}</strong>
                {(selectedStore.branches || []).map((branch) => (
                  <label
                    key={branch.branchId}
                    className="billing-branch-option"
                  >
                    <input
                      type="checkbox"
                      checked={branchIds.includes(branch.branchId)}
                      onChange={() => toggleBranch(branch.branchId)}
                    />
                    <span>{branch.name}</span>
                  </label>
                ))}
              </div>
            )}
            <Button
              appearance="outline"
              loading={previewing}
              disabled={!hasValidPeriod}
              onClick={loadPreview}
            >
              {text.subscriptions.previewPricing}
            </Button>
            {preview?.pricingPreview && (
              <div className="billing-preview-box">
                <div>
                  <span>{text.subscriptions.selectedBranches}</span>
                  <strong>{preview.pricingPreview.coveredBranchCount}</strong>
                </div>
                <div>
                  <span>{text.subscriptions.includedBranches}</span>
                  <strong>{preview.pricingPreview.includedBranchCount}</strong>
                </div>
                <div>
                  <span>{text.subscriptions.extraBranches}</span>
                  <strong>{preview.pricingPreview.extraBranchCount}</strong>
                </div>
                <div>
                  <span>{text.common.subtotal}</span>
                  <strong>
                    {formatSar(preview.pricingPreview.subtotalMinor, lang)}
                  </strong>
                </div>
                <div>
                  <span>{text.common.vat}</span>
                  <strong>
                    {formatSar(preview.pricingPreview.vatAmountMinor, lang)}
                  </strong>
                </div>
                <div>
                  <span>{text.common.total}</span>
                  <strong>
                    {formatSar(preview.pricingPreview.totalMinor, lang)}
                  </strong>
                </div>
              </div>
            )}
            <div className="billing-modal-actions">
              <Button
                appearance="filled"
                loading={saving}
                disabled={!preview}
                onClick={createDraft}
              >
                {text.subscriptions.createDraft}
              </Button>
              <Button appearance="ghost" onClick={onCancel}>
                {text.common.cancel}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateSubscriptionModal;
